import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import type { ContentfulAlertBannerFragment } from '@/graphql/contentful/api';
import { useIsomorphicLayoutEffect } from '@/hooks/useIsomorphicLayoutEffect';
import { useWindowWidth } from '@/hooks/useWindowWidth';
import { COMPONENT_RENDERER_OPTIONS } from '@/integrations/contentful';
import { commonMessages } from '@/messages/common';

import { Box } from '../Box';

import * as S from './AlertBanner.styles';

export interface AlertBannerProps {
  alertBanner?: ContentfulAlertBannerFragment[];
  closeAlertBanner: () => void;
  hasCookieAlertBanner?: boolean;
  isMobileMenuOpen: boolean;
  shoppingBagDrawerOpen: boolean;
}

export const AlertBanner = ({
  alertBanner = [],
  closeAlertBanner,
  hasCookieAlertBanner,
  isMobileMenuOpen,
  shoppingBagDrawerOpen,
}: AlertBannerProps) => {
  const { formatMessage } = useIntl();
  const [textIndex, setTextIndex] = useState<number>(0);
  const [isShowFulltext, setIsShowFulltext] = useState<boolean>(false);
  const [isShowReadmore, setIsShowReadmore] = useState<boolean>(false);
  const [isBannerHidden, setIsBannerHidden] = useState(hasCookieAlertBanner);
  const [hasMounted, setHasMounted] = useState(false);
  const alertTextRef = useRef<HTMLElement>(null);
  const textWrapperRef = useRef<HTMLElement>(null);
  const resize = useWindowWidth();

  const measureDimensions = () => {
    const alertTextElement = alertTextRef.current;
    const textWrapperElement = textWrapperRef.current;

    if (textWrapperElement && alertTextElement) {
      const alertTextWidth = alertTextElement.clientWidth;
      const computedStyle = getComputedStyle(textWrapperElement);
      const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
      const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
      const textWrapperContentWidth =
        textWrapperElement.clientWidth - paddingLeft - paddingRight;

      setIsShowReadmore(alertTextWidth >= textWrapperContentWidth - 22);
    }
  };

  useIsomorphicLayoutEffect(() => {
    measureDimensions();
  }, [textIndex, resize]);

  useEffect(() => {
    setHasMounted(true);
    setIsBannerHidden(hasCookieAlertBanner);
  }, [hasCookieAlertBanner]);

  const finalAlertBanner = alertBanner?.filter((item) => !!item) ?? [];

  if (!finalAlertBanner || finalAlertBanner.length == 0) {
    return null;
  }

  const handleTextIndex = () => {
    setIsShowFulltext(false);
    setIsShowReadmore(false);

    if (textIndex === finalAlertBanner.length - 1) setTextIndex(0);
    else setTextIndex((prev) => prev + 1);
  };

  return (
    <S.AlertBannerWrapper
      $displayAlertBannerWrapper={isMobileMenuOpen || shoppingBagDrawerOpen}
      $isShowFulltext={isShowFulltext}
      className={`${hasMounted && !isBannerHidden ? 'showBanner' : ''}`}
      data-test-id='AlertBanner'
    >
      <S.InnerWrapper $isShowFullText={isShowFulltext}>
        <Box>
          {finalAlertBanner.length > 1 && (
            <S.IconWrapper
              data-testid='AlertBannerArrowIcon'
              onClick={handleTextIndex}
            >
              <S.LeftIcon />
            </S.IconWrapper>
          )}
        </Box>
        <S.TextWrapper
          ref={textWrapperRef}
          showReadMore={!isShowFulltext && isShowReadmore}
        >
          <S.TextInfo showReadMore={!isShowFulltext && isShowReadmore}>
            <Box>
              {documentToReactComponents(
                finalAlertBanner[textIndex]?.alertBannerText?.json,
                COMPONENT_RENDERER_OPTIONS,
              )}
            </Box>
          </S.TextInfo>
          {!isShowFulltext && isShowReadmore && (
            <S.ReadMore
              data-test-id='AlertBannerReadMore'
              onClick={() => setIsShowFulltext(true)}
            >
              {formatMessage(commonMessages.readMore)}
            </S.ReadMore>
          )}
          {/* Invisible element to calculate the text width */}
          <S.TextInfoInvisible ref={alertTextRef}>
            {documentToReactComponents(
              finalAlertBanner[textIndex]?.alertBannerText?.json,
              COMPONENT_RENDERER_OPTIONS,
            )}
          </S.TextInfoInvisible>
        </S.TextWrapper>
        <Box>
          <S.IconWrapper
            data-test-id='AlertBannerCloseIcon'
            onClick={closeAlertBanner}
          >
            <S.CloseIcon />
          </S.IconWrapper>
        </Box>
      </S.InnerWrapper>
    </S.AlertBannerWrapper>
  );
};
